import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { LOGO_URL } from "@/data/content";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Menu", href: "#menu" },
  { label: "Bar", href: "#bar" },
  { label: "Gallery", href: "#gallery" },
  { label: "Catering", href: "#catering" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-[999] transition-all duration-500 ${
          scrolled
            ? "bg-[#0D0D0D]/90 backdrop-blur-xl border-b border-[#C8A24A]/20 py-3"
            : "bg-transparent py-5"
        }`}
      >
        <nav className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between">
          <a href="#home" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img
              src={LOGO_URL}
              alt="Ekaa"
              className="h-11 w-11 md:h-12 md:w-12 object-cover rounded-full ring-1 ring-[#C8A24A]/50 group-hover:ring-[#C8A24A] transition-all"
            />
            <div className="leading-none">
              <span className="font-display text-2xl md:text-[26px] tracking-wide text-gold-gradient">Ekaa</span>
              <span className="block text-[9px] md:text-[10px] tracking-[0.32em] uppercase text-[#D6D6D6]/70 mt-0.5">
                Flavors Meet Elegance
              </span>
            </div>
          </a>

          <ul className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="text-sm tracking-[0.12em] uppercase text-[#D6D6D6] hover:text-[#C8A24A] transition-colors relative after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-[#C8A24A] after:transition-all hover:after:w-full"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href="#order"
              data-testid="nav-order-online"
              className="text-sm tracking-[0.12em] uppercase text-[#C8A24A] hover:text-[#E0C067] transition-colors"
            >
              Order Online
            </a>
            <a
              href="tel:+15488898500"
              data-testid="nav-call-now"
              className="btn-gold group flex items-center gap-2 border border-[#C8A24A] px-6 py-2.5 rounded-sm text-sm tracking-[0.14em] uppercase text-[#C8A24A] hover:bg-gradient-to-r hover:from-[#C8A24A] hover:to-[#E0C067] hover:text-[#0D0D0D] font-medium"
            >
              <Phone className="h-4 w-4" /> Call Now
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(true)}
            className="lg:hidden text-[#C8A24A] p-2"
            aria-label="Open menu"
          >
            <MenuIcon className="h-7 w-7" />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-[1000] bg-[#0D0D0D]/98 backdrop-blur-xl flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#C8A24A]/20">
              <span className="font-display text-2xl text-gold-gradient">Ekaa</span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-[#C8A24A] p-2" aria-label="Close menu">
                <X className="h-7 w-7" />
              </button>
            </div>
            <ul className="flex flex-col items-center justify-center gap-8 flex-1">
              {[...LINKS, { label: "Order Online", href: "#order" }].map((l, i) => (
                <motion.li
                  key={l.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="font-display text-3xl text-[#D6D6D6] hover:text-[#C8A24A] transition-colors"
                  >
                    {l.label}
                  </a>
                </motion.li>
              ))}
              <a
                href="tel:+15488898500"
                onClick={() => setOpen(false)}
                className="mt-4 flex items-center gap-2 border border-[#C8A24A] px-8 py-3 rounded-sm text-[#C8A24A] uppercase tracking-widest"
              >
                <Phone className="h-4 w-4" /> Call Now
              </a>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
